import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAlert } from "react-alert";
import {
    Box,
    Button,
    Grid,
    TextField,
    MenuItem,
    Switch,
    CircularProgress,
    Stack,
    Container,
    Collapse,
    IconButton,
} from "@mui/material";
import {
    Save as SaveIcon,
    ArrowBack as ArrowBackIcon,
    BadgeOutlined as IdentityIcon,
    DescriptionOutlined as SubscriptionIcon,
    TuneOutlined as ConfigIcon,
    IntegrationInstructionsOutlined as IntegrationIcon,
    BoltOutlined as ServiceIcon,
    PaletteOutlined as DesignIcon,
    History as HistoryIcon,
    ExpandMore,
    ExpandLess,
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as RadioButtonUncheckedIcon,
    ExtensionOutlined as AddonIcon,
    CalendarToday as DateIcon,
    Title as TitleIcon,
    Tag as NumberIcon,
    PermIdentity as UserIcon,
    Key as PasswordIcon,
    Badge as BadgeIcon,
    Numbers as IdIcon,
    EmailOutlined as EmailIcon,
    SupportAgent as SupportIcon,
    Category as CategoryIcon,
    LocalOfferOutlined as TagIcon,
    ConfirmationNumber as TicketIcon,
    AccountCircle as AccountIcon,
    Speed as ScoreIcon,
    Link as LinkIcon,
    Dns as ServerIcon,
    Description as ReportIcon,
    Business as OrgIcon,
    Place as CenterIcon
} from "@mui/icons-material";
import { SUBSCRIPTION_PLANS, PLAN_ORDER } from "../../data/subscriptionPlans";
import InputAdornment from '@mui/material/InputAdornment';
import "./SubscriptionNew.css";

const ALLOWED_ADDONS = [
    "Body Summary", "Report Summary", "Test Recommendation", "Quick Summary", 
    "Cover Page", "Marketing Page", "Risk score", "Historical",
    "Diet Page", "Doctor's page", "Consolidated page"
];

const ADDON_DISPLAY_TO_KEY = {
    "Body Summary": "bodySummary",
    "Report Summary": "reportSummary",
    "Test Recommendation": "additionalTestRecommendation",
    "Quick Summary": "quickSummary",
    "Cover Page": "coverPage",
    "Marketing Page": "marketingPage",
    "Risk score": "riskScore",
    "Historical": "historical",
    "Diet Page": "dietPage",
    "Doctor's page": "doctorsPage",
    "Consolidated page": "consolidatedPage"
};

function reportAddonsFromApiAddOns(addOns) {
    if (!addOns || typeof addOns !== "object") return [];
    return ALLOWED_ADDONS.filter((displayName) => {
        const key = ADDON_DISPLAY_TO_KEY[displayName];
        return key && addOns[key] === true;
    });
}

// Helper Components
const ModernInput = ({ label, ...props }) => (
    <div className="input-group">
        <label className="input-label">{label}</label>
        <TextField
            {...props}
            hiddenLabel
            fullWidth
            size="small"
            variant="outlined"
            className="modern-input"
            InputProps={{ ...props.InputProps }}
        />
    </div>
);

const ModernSelect = ({
    label,
    children,
    InputProps,
    startAdornment,
    MenuProps,
    renderValue,
    displayEmpty,
    ...rest
}) => {
    const inputAdornmentProps = InputProps ?? (startAdornment ? { startAdornment } : undefined);
    return (
        <div className="input-group">
            <label className="input-label">{label}</label>
            <TextField
                select
                {...rest}
                fullWidth
                size="small"
                variant="outlined"
                hiddenLabel
                className="modern-input"
                {...(inputAdornmentProps && { InputProps: inputAdornmentProps })}
                SelectProps={{
                    MenuProps,
                    renderValue,
                    displayEmpty,
                }}
            >
                {children}
            </TextField>
        </div>
    );
};

const ModernCard = ({ title, icon, children, className = "" }) => (
    <div className={`modern-card ${className}`}>
        {title && (
            <div className="card-header-row">
                <div className="card-title">
                    <span className="card-icon">{icon}</span>
                    {title}
                </div>
            </div>
        )}
        {children}
    </div>
);

const SubscriptionNew = () => {
    const alert = useAlert();
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const userIdParam = searchParams.get("userId");

    const [tabValue, setTabValue] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [dynamicAllowedAddons, setDynamicAllowedAddons] = useState([...ALLOWED_ADDONS]);
    const [newAddonText, setNewAddonText] = useState("");

    // Form State
    const [formData, setFormData] = useState({
        userId: "", clientId: "", org: "", center: "", customerName: "", password: "",
        lisHisName: "", liveDate: "",
        subscriptionStatus: "", planType: "", displaySubscription: "", reportsSubscribed: "",
        startDate: "", endDate: "",
        prevStartDate: "", prevEndDate: "", savePreviousDates: true,
        clientType: "", serviceType: "", integrationType: "", numberOfTickets: "",
        codebaseTag: "",
        zohobooks: { customerId: "", dueThreshold: "" },
        lamdbaDetails: { name: "", link: "" },
        endpoints: { smartReport: "" },
        reportType: "", reportAddons: [], npsScore: "",
        dashboardAccess: false, portalAccess: false, smartReportAccess: false,
        mailID: "",
        vizApp: false, aiVideoReports: false, languageSupport: false, whatsappChatbot: false, analyticsDashboard: false,
        contentCustomisation: false, contentProfile: false, contentParameter: false, designCustomisation: false,
    });

    const [originalData, setOriginalData] = useState(null);

    useEffect(() => {
        if (userIdParam) {
            fetchSubscriptionDetails(userIdParam);
        } else {
            alert.error("No User ID provided!");
            setIsLoading(false);
        }
    }, [userIdParam]);

    const fetchSubscriptionDetails = async (uid) => {
        setIsLoading(true);
        try {
            const url = `https://rnxsohimg1.execute-api.ap-south-1.amazonaws.com/Production/subscription/status?userId=${uid}`;
            const response = await axios.get(url);

            if (response.data && response.data.data) {
                const data = response.data.data;
                setOriginalData(data);
                const historyStart = data.subscriptionHistory?.[0]?.startDate || "";
                const historyEnd = data.subscriptionHistory?.[0]?.endDate || "";

                setFormData({
                    userId: data.userId || "", clientId: data.clientId || "", org: data.org || "", center: data.center || "", customerName: data.customerName || "",
                    password: data.password || "",
                    lisHisName: data.lisHisName || "",
                    liveDate: data.liveDate ? data.liveDate.split(" ")[0] : "",
                    startDate: data.startDate || "", endDate: data.endDate || "",
                    subscriptionStatus: data.subscriptionStatus || "inactive",
                    prevStartDate: historyStart, prevEndDate: historyEnd, savePreviousDates: true,
                    planType: data.planType || "",
                    displaySubscription: data.subscription || "",
                    reportsSubscribed: data.reportsSubscribed || "",
                    clientType: data.clientType || "",
                    serviceType: (() => {
                        const srv = data.serviceType;
                        const normalized = typeof srv === "string" ? srv.toString().trim().toLowerCase() : "";
                        return normalized === "portal" ? "Portal" : normalized === "api" ? "API" : (srv || "");
                    })(),
                    integrationType: data.integrationType || "", numberOfTickets: data.numberOfTickets || "",
                    codebaseTag: (data.codebaseTag || "").toString(),
                    zohobooks: { customerId: data.zohobooks?.customerId || data.zohobooks?.customer_id || "", dueThreshold: data.zohobooks?.dueThreshold || data.zohobooks?.due_threshold || "" },
                    lamdbaDetails: { name: data.lamdbaDetails?.name || "", link: data.lamdbaDetails?.link || "" },
                    endpoints: { smartReport: data.endpoints?.smartReport || "" },
                    reportType: data.reportSubscription?.reportType || data.reportType || "", 
                    reportAddons: (() => {
                        let loadedAddons = reportAddonsFromApiAddOns(data.reportSubscription?.addOns);
                        if (Array.isArray(data.reportAddons)) {
                            data.reportAddons.forEach(addon => {
                                if (!loadedAddons.includes(addon)) loadedAddons.push(addon);
                            });
                        }
                        
                        // Merge any custom addons into dynamicAllowedAddons
                        setDynamicAllowedAddons(prev => {
                            const newAddons = new Set(prev);
                            loadedAddons.forEach(a => newAddons.add(a));
                            return Array.from(newAddons);
                        });
                        return loadedAddons;
                    })(),
                    mailID: data.notifications?.mailIds ? (Array.isArray(data.notifications.mailIds) ? data.notifications.mailIds.join(",") : data.notifications.mailIds) : "",
                    dashboardAccess: data.accessTo?.dashBoard || false, portalAccess: data.accessTo?.portal || false, smartReportAccess: data.accessTo?.smartReport || false,
                    vizApp: data.reportSubscription?.personalisedServices?.vizapp || false,
                    aiVideoReports: data.reportSubscription?.personalisedServices?.videoreport || false,
                    languageSupport: data.reportSubscription?.personalisedServices?.languageSupport || false,
                    whatsappChatbot: data.reportSubscription?.personalisedServices?.whatsappChatbot || false,
                    analyticsDashboard: data.reportSubscription?.personalisedServices?.analyticsDashboard || false,
                    contentCustomisation: !!(data.reportSubscription?.customisation?.content?.profile || data.reportSubscription?.customisation?.content?.parameter),
                    contentProfile: data.reportSubscription?.customisation?.content?.profile || false,
                    contentParameter: data.reportSubscription?.customisation?.content?.parameter || false,
                    designCustomisation: data.reportSubscription?.customisation?.design || false,
                    npsScore: data.npsScore !== null && data.npsScore !== undefined ? data.npsScore : "",
                });
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            alert.error("Failed to fetch client details");
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
    const handleNestedChange = (parent, field, value) => setFormData(prev => ({ ...prev, [parent]: { ...prev[parent], [field]: value } }));
    const handleAddonToggle = (addonName) => {
        setFormData((prev) => {
            const current = prev.reportAddons || [];
            return current.includes(addonName) ? { ...prev, reportAddons: current.filter((a) => a !== addonName) } : { ...prev, reportAddons: [...current, addonName] };
        });
    };

    const handleSave = async () => {
        setIsSaving(true);
        const confirmStartDate = formData.startDate
            ? (formData.startDate.includes("00:00:00.000") ? formData.startDate : `${formData.startDate} 00:00:00.000`)
            : "";
        const confirmEndDate = formData.endDate
            ? (formData.endDate.includes("23:59:59.000") ? formData.endDate : `${formData.endDate} 23:59:59.000`)
            : "";
        const confirmPrevStart = formData.prevStartDate
            ? (!formData.prevStartDate.includes("00:00:00.000") ? `${formData.prevStartDate} 00:00:00.000` : formData.prevStartDate)
            : "";
        const confirmPrevEnd = formData.prevEndDate
            ? (!formData.prevEndDate.includes("23:59:59.000") ? `${formData.prevEndDate} 23:59:59.000` : formData.prevEndDate)
            : "";
        const prevSubscriptionDetails = formData.savePreviousDates ? [{ startDate: confirmPrevStart, endDate: confirmPrevEnd }] : [];

        const addOnsObjectPayload = {};
        
        // Save dynamically allowed addons (standard and custom)
        dynamicAllowedAddons.forEach((displayName) => {
            // First check if it's a standard addon with a predefined key
            let key = ADDON_DISPLAY_TO_KEY[displayName];
            
            // If it's a custom addon, generate a camelCase key dynamically
            if (!key) {
                key = displayName.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
                    return index === 0 ? word.toLowerCase() : word.toUpperCase();
                }).replace(/[^a-zA-Z0-9]/g, ''); // Remove spaces and special chars
            }
            
            if (key) {
                addOnsObjectPayload[key] = formData.reportAddons.includes(displayName);
            }
        });

        const trimmedMailID = (formData.mailID || "").trim();
        const mailIds = trimmedMailID === ""
            ? []
            : trimmedMailID.split(",").map((id) => id.trim()).filter((id) => id !== "");

        const payload = {
            ...originalData,
            userId: formData.userId, clientId: formData.clientId, org: formData.org, center: formData.center, customerName: formData.customerName, password: formData.password,
            lisHisName: formData.lisHisName || "", liveDate: formData.liveDate || "",
            startDate: confirmStartDate, 
            endDate: confirmEndDate, 
            endpoints: { ...(originalData?.endpoints || {}), smartReport: formData.endpoints?.smartReport || "" },
            subscriptionStatus: formData.subscriptionStatus,
            planType: formData.planType, subscription: formData.displaySubscription, reportsSubscribed: formData.reportsSubscribed,
            clientType: formData.clientType, serviceType: formData.serviceType, integrationType: formData.integrationType, numberOfTickets: formData.numberOfTickets ? parseInt(formData.numberOfTickets) : 0,
            codebaseTag: (formData.codebaseTag || "").trim(),
            savePreviousDates: formData.savePreviousDates, prevSubscriptionDetails: prevSubscriptionDetails,
            accessTo: { dashBoard: formData.dashboardAccess, portal: formData.portalAccess, smartReport: formData.smartReportAccess },
            notifications: { ...originalData?.notifications, mailIds },
            reportAddons: formData.reportAddons,
            reportSubscription: {
                reportType: formData.reportType, addOns: addOnsObjectPayload,
                personalisedServices: { vizapp: formData.vizApp, videoreport: formData.aiVideoReports, languageSupport: formData.languageSupport, whatsappChatbot: formData.whatsappChatbot, analyticsDashboard: formData.analyticsDashboard },
                customisation: { content: formData.contentCustomisation ? { profile: formData.contentProfile, parameter: formData.contentParameter } : { profile: false, parameter: false }, design: formData.designCustomisation },
            },
            npsScore: formData.npsScore === "" ? null : Number(formData.npsScore),
            zohobooks: formData.zohobooks, lamdbaDetails: formData.lamdbaDetails,
        };

        try {
            const updateUrl = "https://rnxsohimg1.execute-api.ap-south-1.amazonaws.com/Production/subscription/update";
            const response = await axios.post(updateUrl, payload);
            if (response.status === 200 || response.data) {
                alert.success("Subscription updated!");
                fetchSubscriptionDetails(formData.userId);
                try { window.dispatchEvent(new CustomEvent("subscription-updated")); } catch (_) { }
            } else { alert.error("Update failed"); }
        } catch (error) { console.error("Error", error); alert.error(`Update failed: ${error.message}`); } finally { setIsSaving(false); }
    };

    if (isLoading) return <Box sx={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}><CircularProgress /></Box>;

    return (
        <div className="subscription-page">
            {/* Fixed Header */}
            <div className="fixed-header">
                <div className="header-content">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <IconButton onClick={() => navigate(-1)} className="btn-secondary" size="small"><ArrowBackIcon fontSize="small" /></IconButton>
                        <div>
                            <div className="customer-title">{formData.customerName || "Subscription"}</div>
                            <div className="customer-meta">{formData.org} • {formData.center} <span className={`status-pill ${formData.subscriptionStatus?.replace(/\s/g, '')}`}>{formData.subscriptionStatus}</span></div>
                        </div>
                    </div>
                    <Button startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} disabled={isSaving} className="btn-primary" onClick={handleSave}>
                        {isSaving ? "Saving..." : "Save Changes"}
                    </Button>
                </div>
                {/* Navigation Tabs */}
                <div className="tabs-container">
                    {[{ label: "Overview", icon: <IdentityIcon /> }, { label: "Features", icon: <ServiceIcon /> }, { label: "Config", icon: <ConfigIcon /> }, { label: "Integrations", icon: <IntegrationIcon /> }].map((tab, index) => (
                        <div key={index} className={`modern-tab ${tabValue === index ? 'active' : ''}`} onClick={() => setTabValue(index)}>
                            {tab.label}
                        </div>
                    ))}
                </div>
            </div>

            <Container maxWidth="xl">
                {/* Tab 0: Overview */}
                {tabValue === 0 && (
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <ModernCard title="Identity" icon={<IdentityIcon />}>
                                <div className="identity-grid">
                                    <div className="identity-full">
                                        <ModernInput
                                            label="Customer Name"
                                            value={formData.customerName}
                                            onChange={(e) => handleChange("customerName", e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><BadgeIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <ModernInput
                                            label="Org"
                                            disabled
                                            value={formData.org}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><OrgIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <ModernInput
                                            disabled
                                            label="Center"
                                            value={formData.center}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><CenterIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </div>
                                    <div className="identity-full">
                                        <ModernInput
                                            label="Password"
                                            disabled
                                            value={formData.password}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><PasswordIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <ModernInput
                                            disabled
                                            label="User ID"
                                            value={formData.userId}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><UserIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <ModernInput
                                            disabled
                                            label="Client ID"
                                            value={formData.clientId}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><IdIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </div>
                                    <div className="identity-full">
                                        <ModernInput
                                            label="LIS / HIS Name"
                                            value={formData.lisHisName}
                                            onChange={(e) => handleChange("lisHisName", e.target.value)}
                                            placeholder="Your LIS/HIS Name Here"
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><BadgeIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </div>
                                </div>
                            </ModernCard>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <ModernCard title="Subscription" icon={<SubscriptionIcon />}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={3}>
                                        <ModernSelect label="Status" value={formData.subscriptionStatus} onChange={(e) => handleChange("subscriptionStatus", e.target.value)} MenuProps={{ className: "modern-select-menu", disableScrollLock: true }}>
                                            <MenuItem value="live">Live</MenuItem>
                                            <MenuItem value="on hold">On Hold</MenuItem>
                                            <MenuItem value="inactive">Inactive</MenuItem>
                                        </ModernSelect>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <ModernSelect label="Plan Type" value={formData.planType} onChange={(e) => handleChange('planType', e.target.value)} MenuProps={{ className: "modern-select-menu", disableScrollLock: true }}>
                                            {PLAN_ORDER.map(key => <MenuItem key={key} value={SUBSCRIPTION_PLANS[key].planName}>{SUBSCRIPTION_PLANS[key].planName}</MenuItem>)}
                                        </ModernSelect>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <ModernInput
                                            label="Display Title"
                                            value={formData.displaySubscription}
                                            onChange={(e) => handleChange("displaySubscription", e.target.value)}
                                            placeholder="e.g. Pro"
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><TitleIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <ModernInput
                                            label="Reports Subscribed"
                                            type="number"
                                            value={formData.reportsSubscribed}
                                            onChange={(e) => handleChange("reportsSubscribed", e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><NumberIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <ModernInput
                                            label="Start Date"
                                            type="date"
                                            value={formData.startDate ? formData.startDate.split(" ")[0] : ""}
                                            onChange={(e) => handleChange("startDate", e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><DateIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <ModernInput
                                            label="Live Date"
                                            type="date"
                                            value={formData.liveDate ? formData.liveDate.split(" ")[0] : ""}
                                            onChange={(e) => handleChange("liveDate", e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><DateIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <ModernInput
                                            label="End Date"
                                            type="date"
                                            value={formData.endDate ? formData.endDate.split(" ")[0] : ""}
                                            onChange={(e) => handleChange("endDate", e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><DateIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <div className={`toggle-row-clean ${formData.savePreviousDates ? 'active' : ''}`} style={{ marginBottom: 0 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Snapshot History</span>
                                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Save dates to history?</span>
                                            </div>
                                            <Switch checked={formData.savePreviousDates} onChange={(e) => handleChange('savePreviousDates', e.target.checked)} />
                                        </div>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <div className="history-block">
                                            <div onClick={() => setShowHistory(!showHistory)} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: showHistory ? '16px' : '0' }}>
                                                {showHistory ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                <span style={{ fontSize: '0.75rem', fontWeight: 700, marginLeft: '8px', color: '#475569', textTransform: 'uppercase' }}>Previous History</span>
                                            </div>
                                            <Collapse in={showHistory}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <ModernInput
                                                            label="Prev Start"
                                                            type="date"
                                                            value={formData.prevStartDate ? formData.prevStartDate.split(" ")[0] : ""}
                                                            onChange={(e) => handleChange("prevStartDate", e.target.value)}
                                                            InputProps={{
                                                                startAdornment: <InputAdornment position="start"><DateIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <ModernInput
                                                            label="Prev End"
                                                            type="date"
                                                            value={formData.prevEndDate ? formData.prevEndDate.split(" ")[0] : ""}
                                                            onChange={(e) => handleChange("prevEndDate", e.target.value)}
                                                            InputProps={{
                                                                startAdornment: <InputAdornment position="start"><DateIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Collapse>
                                        </div>
                                    </Grid>
                                </Grid>
                            </ModernCard>
                        </Grid>
                    </Grid>
                )}

                {/* Tab 1: Features */}
                {tabValue === 1 && (
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <ModernCard title="Services" icon={<ServiceIcon />}>
                                <Stack spacing={0}>
                                    {[
                                        { key: "vizApp", label: "Viz App", desc: "Advanced visualization components" },
                                        { key: "aiVideoReports", label: "Smart Video Report", desc: "AI video generation" },
                                        { key: "languageSupport", label: "Language Support", desc: "Multi-language" },
                                        { key: "whatsappChatbot", label: "WhatsApp Chatbot", desc: "Automated chat" },
                                        { key: "analyticsDashboard", label: "Analytics", desc: "Deep insights" },
                                    ].map(item => (
                                        <div key={item.key} className="toggle-row-clean">
                                            <div>
                                                <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>{item.label}</div>
                                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{item.desc}</div>
                                            </div>
                                            <Switch checked={formData[item.key]} onChange={(e) => handleChange(item.key, e.target.checked)} />
                                        </div>
                                    ))}
                                </Stack>
                            </ModernCard>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ModernCard title="Customisation" icon={<DesignIcon />}>
                                <div className="toggle-row-clean">
                                    <div>
                                        <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>Design Customisation</div>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Colors and Branding</div>
                                    </div>
                                    <Switch checked={formData.designCustomisation} onChange={(e) => handleChange("designCustomisation", e.target.checked)} />
                                </div>
                                <div className="toggle-row-clean">
                                    <div>
                                        <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>Content Customisation</div>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Report structure</div>
                                    </div>
                                    <Switch checked={formData.contentCustomisation} onChange={(e) => handleChange("contentCustomisation", e.target.checked)} />
                                </div>
                                {formData.contentCustomisation && (
                                    <div style={{ marginLeft: '16px', paddingLeft: '16px', borderLeft: '2px solid #e2e8f0' }}>
                                        <div className="toggle-row-clean">
                                            <div style={{ fontSize: '0.9rem' }}>Profile Customisation</div>
                                            <Switch size="small" checked={formData.contentProfile} onChange={(e) => handleChange("contentProfile", e.target.checked)} />
                                        </div>
                                        <div className="toggle-row-clean">
                                            <div style={{ fontSize: '0.9rem' }}>Parameter Customisation</div>
                                            <Switch size="small" checked={formData.contentParameter} onChange={(e) => handleChange("contentParameter", e.target.checked)} />
                                        </div>
                                    </div>
                                )}
                            </ModernCard>
                        </Grid>
                    </Grid>
                )}

                {/* Tab 2: Config */}
                {tabValue === 2 && (
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <ModernCard title="Report Config" className="h-full" icon={<ConfigIcon />}>
                                <Stack spacing={3}>
                                    <ModernSelect
                                        label="Report Type"
                                        value={formData.reportType}
                                        onChange={(e) => handleChange("reportType", e.target.value)}
                                        displayEmpty
                                        renderValue={(v) => v || "Select report type"}
                                        MenuProps={{ className: "modern-select-menu", disableScrollLock: true }}
                                    >
                                        <MenuItem value="Compact">Compact</MenuItem>
                                        <MenuItem value="Personal">Personal</MenuItem>
                                        <MenuItem value="Total">Total</MenuItem>
                                    </ModernSelect>
                                    <ModernInput
                                        label="NPS Score"
                                        type="number"
                                        value={formData.npsScore}
                                        onChange={(e) => handleChange("npsScore", e.target.value)}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start"><ScoreIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                        }}
                                    />
                                </Stack>
                            </ModernCard>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <ModernCard title="Add-ons" icon={<AddonIcon />}>
                                <Grid container spacing={2}>
                                    {dynamicAllowedAddons.map(addon => {
                                        const isSelected = formData.reportAddons.includes(addon);
                                        return (
                                            <Grid style={{ cursor: 'pointer' }} item xs={12} sm={6} md={4} key={addon}>
                                                <div className={`addon-box ${isSelected ? 'selected' : ''}`} onClick={() => handleAddonToggle(addon)}>
                                                    <span style={{ fontSize: '0.85rem', cursor: 'pointer', fontWeight: isSelected ? 600 : 500, color: isSelected ? '#1e40af' : '#334155' }}>{addon}</span>
                                                    {isSelected ? <CheckCircleIcon color="primary" fontSize="small" sx={{ cursor: 'pointer' }}/> : <RadioButtonUncheckedIcon color="disabled" fontSize="small" sx={{ cursor: 'pointer' }}/>}
                                                </div>
                                            </Grid>
                                        )
                                    })}
                                </Grid>
                                
                                {/* Add Custom Add-on Section */}
                                <Box mt={3} pt={2} borderTop="1px dashed #e2e8f0" display="flex" alignItems="center" gap={2}>
                                    <TextField
                                        size="small"
                                        placeholder="Enter custom add-on name"
                                        variant="outlined"
                                        value={newAddonText}
                                        onChange={(e) => setNewAddonText(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && newAddonText.trim()) {
                                                e.preventDefault();
                                                const trimmed = newAddonText.trim();
                                                if (!dynamicAllowedAddons.includes(trimmed)) {
                                                    setDynamicAllowedAddons(prev => [...prev, trimmed]);
                                                }
                                                if (!formData.reportAddons.includes(trimmed)) {
                                                    setFormData(prev => ({ ...prev, reportAddons: [...prev.reportAddons, trimmed] }));
                                                }
                                                setNewAddonText("");
                                            }
                                        }}
                                        sx={{ 
                                            width: '250px',
                                            '& .MuiOutlinedInput-root': { borderRadius: '8px' }
                                        }}
                                    />
                                    <Button 
                                        variant="outlined" 
                                        size="small"
                                        onClick={() => {
                                            if (newAddonText.trim()) {
                                                const trimmed = newAddonText.trim();
                                                if (!dynamicAllowedAddons.includes(trimmed)) {
                                                    setDynamicAllowedAddons(prev => [...prev, trimmed]);
                                                }
                                                if (!formData.reportAddons.includes(trimmed)) {
                                                    setFormData(prev => ({ ...prev, reportAddons: [...prev.reportAddons, trimmed] }));
                                                }
                                                setNewAddonText("");
                                            }
                                        }}
                                        disabled={!newAddonText.trim()}
                                        sx={{ borderRadius: '8px', textTransform: 'none', px: 2 }}
                                    >
                                        Add Custom Add-on
                                    </Button>
                                </Box>
                            </ModernCard>
                        </Grid>
                    </Grid>
                )}

                {/* Tab 3: Integrations */}
                {tabValue === 3 && (
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <ModernCard title="Access" className="h-full" icon={<IdentityIcon />}>
                                {['dashboardAccess', 'portalAccess'].map(key => (
                                    <div key={key} className="toggle-row-clean">
                                        <div>
                                            <div style={{ fontSize: '0.9rem', fontWeight: 600, textTransform: 'capitalize' }}>{key.replace('Access', '')}</div>
                                        </div>
                                        <Switch checked={formData[key]} onChange={(e) => handleChange(key, e.target.checked)} />
                                    </div>
                                ))}
                            </ModernCard>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <ModernCard title="Technical" icon={<IntegrationIcon />}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <ModernInput
                                            label="Notification Emails"
                                            multiline
                                            rows={1}
                                            value={formData.mailID}
                                            onChange={(e) => handleChange("mailID", e.target.value)}
                                            placeholder="email@example.com or comma-separated"
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <ModernInput
                                            label="Smart Report Endpoint"
                                            value={formData.endpoints.smartReport}
                                            onChange={(e) => handleNestedChange("endpoints", "smartReport", e.target.value)}
                                            placeholder="https://..."
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><LinkIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <ModernSelect
                                            label="Integration Type"
                                            value={formData.integrationType || ""}
                                            onChange={(e) => handleChange("integrationType", e.target.value)}
                                            displayEmpty
                                            renderValue={(v) => v || "Select integration"}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><IntegrationIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                            MenuProps={{ className: "modern-select-menu", disableScrollLock: true }}
                                        >
                                            <MenuItem value="modelA">Model A</MenuItem>
                                            <MenuItem value="modelB">Model B</MenuItem>
                                        </ModernSelect>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <ModernSelect
                                            label="Service Type"
                                            value={formData.serviceType || ""}
                                            onChange={(e) => handleChange("serviceType", e.target.value)}
                                            displayEmpty
                                            renderValue={(v) => v || "Service"}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><ServerIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                            MenuProps={{ className: "modern-select-menu", disableScrollLock: true }}
                                        >
                                            <MenuItem value="API">API</MenuItem>
                                            <MenuItem value="Portal">Portal</MenuItem>
                                        </ModernSelect>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <ModernSelect
                                            label="Client Type"
                                            value={formData.clientType || ""}
                                            onChange={(e) => handleChange("clientType", e.target.value)}
                                            displayEmpty
                                            renderValue={(v) => v || "Client type"}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><CategoryIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                            MenuProps={{ className: "modern-select-menu", disableScrollLock: true }}
                                        >
                                            <MenuItem value="LIS">LIS</MenuItem>
                                            <MenuItem value="Lab">Lab</MenuItem>
                                        </ModernSelect>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <ModernInput
                                            label="No of Tickets"
                                            type="number"
                                            value={formData.numberOfTickets}
                                            onChange={(e) => handleChange("numberOfTickets", e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><TicketIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <ModernInput
                                            label="Codebase Tag"
                                            value={formData.codebaseTag || ""}
                                            onChange={(e) => handleChange("codebaseTag", e.target.value)}
                                            placeholder="e.g. portal"
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><TagIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </ModernCard>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ModernCard title="Zohobooks" icon={<IntegrationIcon />}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <ModernInput
                                            label="Customer ID"
                                            value={formData.zohobooks.customerId}
                                            onChange={(e) => handleNestedChange('zohobooks', 'customerId', e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><AccountIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <ModernInput
                                            label="Due Threshold"
                                            value={formData.zohobooks.dueThreshold}
                                            onChange={(e) => handleNestedChange('zohobooks', 'dueThreshold', e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><ScoreIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </ModernCard>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ModernCard title="Lambda" icon={<IntegrationIcon />}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <ModernInput
                                            label="Name"
                                            value={formData.lamdbaDetails.name}
                                            onChange={(e) => handleNestedChange('lamdbaDetails', 'name', e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><TitleIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <ModernInput
                                            label="Link"
                                            value={formData.lamdbaDetails.link}
                                            onChange={(e) => handleNestedChange('lamdbaDetails', 'link', e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><LinkIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </ModernCard>
                        </Grid>
                    </Grid>
                )}
            </Container>
        </div>
    );
};

export default SubscriptionNew;
